import { Link } from 'react-router-dom';
import {
  IoChevronForward,
  IoImageOutline,
  IoMailOutline,
  IoGiftOutline,
  IoTrashOutline,
} from 'react-icons/io5';
import type { Draft, DraftProductType } from '@pelipost/consumer-api-client';
import { Button } from '@/components/ui/button';
import { cn } from '@/components/ui/utils';

/**
 * One-line summary of a draft, used on Home's Drafts card and the
 * /app/drafts list page.
 *
 * The full row links to the relevant product flow with the draft id in
 * React Router nav state (so the flow can resume). A small trash icon
 * button fires onDelete without navigating.
 *
 * When a draft has expired or is close to expiring (< 7 days) we surface
 * that next to the timestamp so the user knows to resume soon.
 */

const PRODUCT_META: Record<DraftProductType, { label: string; icon: typeof IoImageOutline; flow: string }> = {
  photo: { label: 'Photo order', icon: IoImageOutline, flow: '/app/products/photos' },
  letter: { label: 'Letter', icon: IoMailOutline, flow: '/app/products/letters' },
  greeting_card: { label: 'Greeting card', icon: IoGiftOutline, flow: '/app/products/cards' },
};

/** Return "2 days ago" / "just now" / etc. from an ISO date. */
function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = now - then;
  const s = Math.floor(diffMs / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
}

/** Days until expiry; negative if already expired. */
function daysUntil(iso: string): number {
  const then = new Date(iso).getTime();
  const now = Date.now();
  return Math.floor((then - now) / (1000 * 60 * 60 * 24));
}

export interface DraftRowProps {
  draft: Draft;
  onDelete?: (draft: Draft) => void;
  deleting?: boolean;
  className?: string;
}

export function DraftRow({ draft, onDelete, deleting = false, className }: DraftRowProps) {
  const meta = PRODUCT_META[draft.productType];
  const Icon = meta.icon;
  const expiresInDays = daysUntil(draft.expiresAt);
  const isExpiring = expiresInDays >= 0 && expiresInDays < 7;
  const isExpired = expiresInDays < 0;

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-4 py-3 min-h-[64px]',
        'border-b border-[var(--border)] last:border-b-0',
        'hover:bg-[var(--background)]',
        className,
      )}
    >
      <span
        className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[var(--brand-light)] text-[var(--brand)] shrink-0"
        aria-hidden
      >
        <Icon size={18} />
      </span>
      <Link
        to={meta.flow}
        state={{ draftId: draft.id }}
        className="flex-1 min-w-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] rounded"
        aria-label={`Resume ${meta.label.toLowerCase()} for ${draft.contactName || 'unknown recipient'}`}
      >
        <p className="font-semibold text-[14px] text-[var(--text)] truncate">
          {meta.label}
          {draft.contactName ? (
            <span className="font-normal text-[var(--text-light)]"> · {draft.contactName}</span>
          ) : null}
        </p>
        <p className="text-[12px] text-[var(--text-light)] truncate">
          {draft.facilityName || 'Recipient removed'}
        </p>
        <p className={cn(
          'text-[12px]',
          isExpired
            ? 'text-[var(--error)]'
            : isExpiring
            ? 'text-[var(--warning)]'
            : 'text-[var(--text-muted)]',
        )}>
          {isExpired
            ? 'Expired'
            : isExpiring
            ? `Expires in ${expiresInDays} day${expiresInDays === 1 ? '' : 's'}`
            : `Saved ${timeAgo(draft.updatedAt)}`}
        </p>
      </Link>
      {onDelete ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0 text-[var(--text-muted)] hover:text-[var(--error)]"
          aria-label={`Delete draft for ${draft.contactName || 'unknown recipient'}`}
          onClick={() => onDelete(draft)}
          disabled={deleting}
        >
          <IoTrashOutline size={18} />
        </Button>
      ) : (
        <Link
          to={meta.flow}
          state={{ draftId: draft.id }}
          className="shrink-0 text-[var(--text-muted)] hover:text-[var(--brand)]"
          aria-hidden
          tabIndex={-1}
        >
          <IoChevronForward size={18} />
        </Link>
      )}
    </div>
  );
}
